/**
 * O botão que termina um pedido parado (`pode_completar` do servidor), com o
 * modal que diz o que ele faz. Três rótulos, um endpoint
 * (`POST /change-requests/{id}/completar-criacao`):
 *
 * - "Completar a criação" (TASK-12154): a criação parada em `apply_parcial`.
 * - "Refazer a rede" (TASK-12167): a criação sem o peering do TGW.
 * - "Reaplicar a partir da master" (consoleai#26379): o ajuste livre em
 *   `apply_parcial`. O pedido novo planeja no código já mergeado, trata como
 *   esperado só o que o pedido parado tinha no plano, e passa pela aprovação
 *   de sempre antes do apply.
 *
 * Quem decide o rótulo é o servidor, pelo ALVO do botão (`completar_refaz_rede`,
 * `completar_reaplica`): a retomada que morreu mostra o botão da origem.
 */
import { useState } from 'react'
import { Button, Group, Modal, Stack, Text } from '@mantine/core'
import { useMutation } from '@tanstack/react-query'
import { completarCriacao, type PedidoDoCartao } from '@/api/terraformRunner'
import { motivoDoErro } from './DecisaoDoPedido'

const BARLOW = '"Barlow", Arial, sans-serif'
const MONO = '"JetBrains Mono", monospace'

export function rotuloDoCompletar(pedido: PedidoDoCartao): string {
  if (pedido.completar_reaplica === true) return 'Reaplicar a partir da master'
  if (pedido.completar_refaz_rede === true) return 'Refazer a rede'
  return 'Completar a criação'
}

interface CompletarDoPedidoProps {
  pedido: PedidoDoCartao
  onFeito: () => void
}

export function CompletarDoPedido({ pedido, onFeito }: CompletarDoPedidoProps) {
  const [aberto, setAberto] = useState(false)
  const [erro, setErro] = useState<string | null>(null)
  const alvo = pedido.completar_pedido_id ?? pedido.id
  const completar = useMutation({
    mutationFn: () => completarCriacao(alvo),
    onSuccess: () => {
      setAberto(false)
      setErro(null)
      onFeito()
    },
    onError: (err) => setErro(motivoDoErro(err)),
  })
  if (!pedido.pode_completar) return null
  const rotulo = rotuloDoCompletar(pedido)

  return (
    <>
      <Group gap="xs" mt="xs">
        <Button size="xs" color="inovareTeal" onClick={() => { setErro(null); setAberto(true) }}>
          {rotulo}
        </Button>
      </Group>
      <Modal opened={aberto} onClose={() => setAberto(false)}
             title={`${rotulo} do pedido #${alvo}`}>
        <Stack gap="sm">
          {pedido.completar_reaplica === true ? (
            <Text size="sm" style={{ fontFamily: BARLOW }}>
              Um plan novo a partir do código já mergeado
              em <span style={{ fontFamily: MONO }}>{pedido.stage_key}</span> aplica só o que
              o pedido #{alvo} deixou pela metade: o mesmo endereço, com a mesma ação. Qualquer
              outra mudança no plan recusa o pedido. Ele passa pela aprovação do DevOps Sênior
              antes do apply.
            </Text>
          ) : pedido.completar_refaz_rede === true ? (
            <Text size="sm" style={{ fontFamily: BARLOW }}>
              Roda de novo só o passo de rede
              de <span style={{ fontFamily: MONO }}>{pedido.stage_key}</span>: aceitar o
              peering do TGW com o devops e criar as rotas. Nada do Terraform é aplicado.
              Rota que já aponta para outro lugar é conflito e não é trocada.
            </Text>
          ) : (
            <Text size="sm" style={{ fontFamily: BARLOW }}>
              Um plan novo a partir da main cria só o que faltou do plano aprovado
              em <span style={{ fontFamily: MONO }}>{pedido.stage_key}</span>. Ele passa
              pela aprovação de novo antes do apply.
            </Text>
          )}
          {erro && <Text size="sm" c="red" style={{ fontFamily: BARLOW }}>{erro}</Text>}
          <Group gap="xs">
            <Button size="xs" color="inovareTeal" loading={completar.isPending}
                    onClick={() => completar.mutate()}>
              Confirmar
            </Button>
            <Button size="xs" variant="subtle" onClick={() => setAberto(false)}>Cancelar</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
